import type { SelectOption } from '@movingimage-evp/mi-ui-component-library';
import type { ChangeEvent } from 'react';

import {
  Heading,
  InputDescription,
  Paragraph,
  Select,
  SelectionInputLayout,
  Toggle,
  classNames,
} from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';

import {
  EventStartVerificationPolicy,
  useGetLsproDefaultSettingsQuery,
  useUpdateDefaultTranscodingProfileMutation,
  useUpdateEventStartVerificationPolicyMutation,
  useUpdateLsproDefaultSettingsMutation,
} from '../../../../generated/graphql-manager';
import { useCurrentUser } from '../../../hooks/current-user';
import { useGetFeatures } from '../../../hooks/use-get-features';
import { useStreamingProfiles } from '../../../hooks/use-streaming-profiles';

import styles from './settings-page.module.css';
import managerStyles from '../../../manager.module.css';

export function SettingsPage() {
  const { lsproId } = useCurrentUser();
  const { t } = useTranslation();
  const { isWatermarkingFeatureEnabled } = useGetFeatures();

  const { data } = useGetLsproDefaultSettingsQuery({ variables: { lsproId } });

  const defaultSettings = data?.lsproSettings?.defaultSettings;
  const defaultProfileId = data?.lsproSettings?.transcodingProfileSettings?.defaultProfileId;
  const customProfiles = data?.lsproSettings?.transcodingProfileSettings?.customProfiles;
  const eventStartVerificationPolicy = data?.lsproSettings?.eventStartVerificationPolicy;

  const [updateLsproDefaultSettings] = useUpdateLsproDefaultSettingsMutation();

  const handleDefaultSettingsChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    updateLsproDefaultSettings({
      variables: {
        input: {
          lsproId,
          [name]: checked,
        },
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateLiveStreamProDefaultSettings: {
          __typename: 'UpdateLiveStreamProDefaultSettingsSuccess',
          defaultSettings: {
            __typename: 'LiveStreamProDefaultSettings',
            ...defaultSettings,
            allowTranscodingProfileSelection: defaultSettings?.allowTranscodingProfileSelection ?? false,
            ingestAuthenticationAsDefault: defaultSettings?.ingestAuthenticationAsDefault ?? false,
            privateEventAsDefault: defaultSettings?.privateEventAsDefault ?? false,
            singleSignOnAsDefault: defaultSettings?.singleSignOnAsDefault ?? false,
            showWatermarkingAsDefault: defaultSettings?.showWatermarkingAsDefault ?? false,
            [name]: checked,
          },
        },
      },
      update: (cache) => {
        cache.modify({
          id: cache.identify({ __typename: 'LiveStreamProSettings', lsproId }),
          fields: {
            defaultSettings: (existingDefaultSettings = {}) => ({
              ...existingDefaultSettings,
              [name]: checked,
            }),
          },
        });
      },
    });
  };

  const [updateDefaultTranscodingProfile] = useUpdateDefaultTranscodingProfileMutation();

  const handleStreamingProfileChange = (option: SelectOption) => {
    updateDefaultTranscodingProfile({
      variables: {
        input: {
          lsproId,
          defaultProfileId: option.key,
        },
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateDefaultTranscodingProfile: {
          __typename: 'UpdateDefaultTranscodingProfileSuccess',
          defaultProfile: {
            __typename: 'TranscodingProfile',
            id: option.key,
          },
        },
      },
      update: (cache) => {
        cache.modify({
          id: cache.identify({ __typename: 'LiveStreamProSettings', lsproId }),
          fields: {
            transcodingProfileSettings: (existingTranscodingProfileSettings = {}) => ({
              ...existingTranscodingProfileSettings,
              defaultProfileId: option.key,
            }),
          },
        });
      },
    });
  };

  const [updateEventStartVerificationPolicy] = useUpdateEventStartVerificationPolicyMutation();

  const handleEventStartVerificationPolicyChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;

    updateEventStartVerificationPolicy({
      variables: {
        input: {
          lsproId,
          policy: checked
            ? EventStartVerificationPolicy.ONLY_ACTIVE_PRIMARY_LANGUAGE_STREAM_REQUIRED
            : EventStartVerificationPolicy.ALL_ACTIVE_LANGUAGE_STREAMS_REQUIRED,
        },
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateEventStartVerificationPolicy: {
          __typename: 'UpdateEventStartVerificationPolicySuccess',
          policy: checked
            ? EventStartVerificationPolicy.ONLY_ACTIVE_PRIMARY_LANGUAGE_STREAM_REQUIRED
            : EventStartVerificationPolicy.ALL_ACTIVE_LANGUAGE_STREAMS_REQUIRED,
        },
      },
      update: (cache) => {
        cache.modify({
          id: cache.identify({ __typename: 'LiveStreamProSettings', lsproId }),
          fields: {
            eventStartVerificationPolicy() {
              return checked
                ? EventStartVerificationPolicy.ONLY_ACTIVE_PRIMARY_LANGUAGE_STREAM_REQUIRED
                : EventStartVerificationPolicy.ALL_ACTIVE_LANGUAGE_STREAMS_REQUIRED;
            },
          },
        });
      },
    });
  };

  const streamingProfiles = useStreamingProfiles();
  const chosenStreamingProfile =
    streamingProfiles.find((option) => option.key === defaultProfileId) || streamingProfiles[0];

  return (
    <main className={classNames(managerStyles.main, styles.wrapper)} data-testid="settings-page">
      <Heading className={managerStyles.grayText}>{t('manager.channelSettings.settings.heading')}</Heading>

      <Paragraph>{t('manager.channelSettings.settings.section.eventAccess')}</Paragraph>

      {defaultSettings?.singleSignOnAsDefault !== undefined && (
        <SelectionInputLayout
          hintMessage={t('manager.channelSettings.settings.sso.description')}
          checked={defaultSettings?.singleSignOnAsDefault}
          variant="ghost"
          inputElement={
            <Toggle
              data-testid="default-settings-sso-toggle"
              name="singleSignOnAsDefault"
              checked={defaultSettings?.singleSignOnAsDefault}
              onChange={handleDefaultSettingsChange}
            >
              {t('manager.channelSettings.settings.sso.label')}
            </Toggle>
          }
        />
      )}

      {defaultSettings?.privateEventAsDefault !== undefined && (
        <SelectionInputLayout
          hintMessage={t('manager.channelSettings.settings.privetEvent.description')}
          checked={defaultSettings?.privateEventAsDefault}
          variant="ghost"
          inputElement={
            <Toggle
              data-testid="default-settings-private-event-toggle"
              name="privateEventAsDefault"
              checked={defaultSettings?.privateEventAsDefault}
              onChange={handleDefaultSettingsChange}
            >
              {t('manager.channelSettings.settings.privetEvent.label')}
            </Toggle>
          }
        />
      )}

      {isWatermarkingFeatureEnabled && (
        <>
          <Paragraph>{t('manager.channelSettings.settings.section.security')}</Paragraph>

          {defaultSettings?.showWatermarkingAsDefault !== undefined && (
            <SelectionInputLayout
              hintMessage={t('manager.channelSettings.settings.watermarking.description')}
              checked={defaultSettings?.showWatermarkingAsDefault}
              variant="ghost"
              inputElement={
                <Toggle
                  data-testid="default-settings-watermarking-toggle"
                  name="showWatermarkingAsDefault"
                  checked={defaultSettings?.showWatermarkingAsDefault}
                  onChange={handleDefaultSettingsChange}
                >
                  {t('manager.channelSettings.settings.watermarking.label')}
                </Toggle>
              }
            />
          )}
        </>
      )}

      <Paragraph>{t('manager.channelSettings.settings.section.streaming')}</Paragraph>

      {defaultSettings?.ingestAuthenticationAsDefault !== undefined && (
        <SelectionInputLayout
          hintMessage={t('manager.channelSettings.settings.ingestAuthentication.description')}
          checked={defaultSettings?.ingestAuthenticationAsDefault}
          variant="ghost"
          inputElement={
            <Toggle
              data-testid="default-settings-ingest-authentication-toggle"
              name="ingestAuthenticationAsDefault"
              checked={defaultSettings?.ingestAuthenticationAsDefault}
              onChange={handleDefaultSettingsChange}
            >
              {t('manager.channelSettings.settings.ingestAuthentication.label')}
            </Toggle>
          }
        />
      )}

      <SelectionInputLayout
        hintMessage={t('manager.channelSettings.settings.eventStartVerificationPolicy.description')}
        checked={
          eventStartVerificationPolicy === EventStartVerificationPolicy.ONLY_ACTIVE_PRIMARY_LANGUAGE_STREAM_REQUIRED
        }
        variant="ghost"
        inputElement={
          <Toggle
            data-testid="event-start-verification-policy-toggle"
            name="eventStartVerificationPolicy"
            checked={
              eventStartVerificationPolicy === EventStartVerificationPolicy.ONLY_ACTIVE_PRIMARY_LANGUAGE_STREAM_REQUIRED
            }
            onChange={handleEventStartVerificationPolicyChange}
          >
            {t('manager.channelSettings.settings.eventStartVerificationPolicy.label')}
          </Toggle>
        }
      />

      {customProfiles && customProfiles.length > 0 && (
        <>
          {chosenStreamingProfile && (
            <InputDescription label={t('manager.channelSettings.settings.streamingProfile.label')}>
              <Select
                data-testid="streaming-profile-select"
                optionsIdProperty="key"
                value={chosenStreamingProfile}
                options={streamingProfiles}
                onChange={handleStreamingProfileChange}
              />
            </InputDescription>
          )}

          {defaultSettings?.allowTranscodingProfileSelection !== undefined && (
            <SelectionInputLayout
              hintMessage={t('manager.channelSettings.settings.allowTranscodingProfileSelection.description')}
              checked={defaultSettings?.allowTranscodingProfileSelection}
              variant="ghost"
              inputElement={
                <Toggle
                  data-testid="allow-transcoding-profile-selection-toggle"
                  name="allowTranscodingProfileSelection"
                  checked={defaultSettings?.allowTranscodingProfileSelection}
                  onChange={handleDefaultSettingsChange}
                >
                  {t('manager.channelSettings.settings.allowTranscodingProfileSelection.label')}
                </Toggle>
              }
            />
          )}
        </>
      )}
    </main>
  );
}
